<template>
  <div class="teams_wrap">
    <div v-if="canWrite" class="node_panel">
      <div class="node_head">
        <span class="node_title">分布式节点</span>
        <el-tag size="mini" :type="onlineNodeCount ? 'success' : 'info'">
          {{ onlineNodeCount }} / {{ nodes.length }} 在线
        </el-tag>
        <el-button type="text" size="mini" :loading="nodeLoading" @click="loadNodes">刷新节点</el-button>
      </div>
      <div class="node_list">
        <el-tag v-for="n in nodes" :key="n.node_id" size="mini"
                :type="n.status === 'active' ? 'success' : 'info'">
          {{ n.name || n.node_id }} · {{ n.url }} · {{ n.status === 'active' ? '在线' : '离线' }}
        </el-tag>
        <span v-if="!nodes.length" class="node_empty">
          暂无已注册节点：分布式成员将按成员地址直连，无地址或无健康节点时自动本地执行
        </span>
      </div>
    </div>

    <div class="team_cards" v-loading="loading">
      <div v-for="row in tableData" :key="row.team_id" class="team_card">
        <div class="card_head">
          <i class="el-icon-s-custom card_icon"></i>
          <span class="card_name" :title="row.name">{{ row.name }}</span>
          <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="mini">
            {{ row.status === 'active' ? '生效' : '归档' }}
          </el-tag>
          <el-tag :type="row.mode === 'distributed' ? 'warning' : ''" size="mini">
            {{ row.mode === 'distributed' ? '分布式' : '本地' }}
          </el-tag>
          <i v-if="canWrite" class="el-icon-delete card_del" title="删除" @click.stop="handleDel(row)"></i>
        </div>
        <div class="card_summary">{{ row.description || '（暂无团队描述）' }}</div>

        <div class="roster_block">
          <div class="roster_leader">
            <span class="roster_k">队长</span>
            <span class="roster_v leader_name">{{ agentName(row.leader_agent_id) }}</span>
          </div>
          <div class="roster_members">
            <span class="roster_k">成员</span>
            <div class="member_list">
              <div v-for="m in row.members || []" :key="m.agent_id" class="member_item">
                <span class="member_name">{{ agentName(m.agent_id) }}</span>
                <span class="member_role">{{ m.role || '未指定分工' }}</span>
                <el-tag v-if="m.node_url" size="mini" type="warning">远端</el-tag>
              </div>
              <span v-if="!(row.members && row.members.length)" class="roster_empty">暂无成员</span>
            </div>
          </div>
        </div>

        <div class="card_meta">
          <div class="meta_row"><span class="meta_k">更新时间</span><span>{{ fmtTime(row.utime) }}</span></div>
        </div>
        <div class="card_actions">
          <el-button type="text" size="small" @click="goChat(row)">去对话</el-button>
          <el-button v-if="canWrite" type="text" size="small" @click="openEdit(row)">编辑</el-button>
          <el-button type="text" size="small" @click="goMembers(row)">团队成员</el-button>
        </div>
      </div>
      <el-empty v-if="!loading && !tableData.length" description="暂无团队数据" class="cards_empty" />
    </div>

    <!-- 底部分页：吸底固定，与订单管理页底部一致 -->
    <div class="pager_bar">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagesize"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      />
    </div>

    <!-- 团队编辑抽屉：与「新增数字员工」一致的右侧弹出样式 -->
    <el-drawer :visible.sync="editVisible" direction="rtl" size="600px" :with-header="false"
               :modal="true" append-to-body :modal-append-to-body="true"
               class="team_drawer" :before-close="closeEdit" @open="raiseDrawer">
      <div class="drawer_head">
        <span>{{ isEdit ? '编辑团队' : '新增团队' }}</span>
        <i class="el-icon-close drawer_close" @click="closeEdit"></i>
      </div>
      <div class="drawer_body">
        <el-alert type="info" :closable="false" class="drawer_tip">
          <div slot="title">
            队长发起的每一轮对话都会进入团队编排：规划器读取下方「成员分工」拆解任务帧并指派成员执行，最后由队长汇聚答复。
            因此分工必须写清职责边界与产出，例如「负责招聘渠道拓展、候选人初筛与面试安排」。
          </div>
        </el-alert>
        <el-form ref="form" :model="form" :rules="rules" label-width="96px" size="small">
          <el-form-item label="团队名称" prop="name">
            <el-input v-model="form.name" maxlength="64" placeholder="同企业下唯一，如：招聘攻坚小组" />
          </el-form-item>
          <el-form-item label="团队描述">
            <el-input v-model="form.description" type="textarea" :rows="2" maxlength="300" />
          </el-form-item>
          <el-form-item label="运行模式">
            <el-radio-group v-model="form.mode">
              <el-radio label="local">本地 Team</el-radio>
              <el-radio label="distributed">分布式 Team</el-radio>
            </el-radio-group>
            <div class="field_tip">
              本地：全部成员都在当前实例执行；分布式：优先按成员地址转发，未填地址时按节点注册表（Agent 亲和 + 心跳健康）路由，
              无可用节点自动回退本地执行。
            </div>
          </el-form-item>
          <el-form-item label="队长" prop="leader_agent_id">
            <el-select v-model="form.leader_agent_id" filterable placeholder="选择队长 Agent"
                       style="width: 100%" @change="onLeaderChange">
              <el-option v-for="a in agentOptions" :key="a.agent_id"
                         :label="a.display_name || a.name" :value="a.agent_id" />
            </el-select>
            <div class="field_tip">队长负责规划汇聚与最终答复；以该员工身份发起对话即自动启用团队协作。</div>
          </el-form-item>
          <el-form-item label="状态">
            <el-radio-group v-model="form.status">
              <el-radio label="active">生效</el-radio>
              <el-radio label="archived">归档</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="成员分工">
            <div class="member_editor">
              <div v-for="(m, idx) in form.members" :key="idx" class="member_row">
                <div class="member_row_head">
                  <span class="member_no">成员 {{ idx + 1 }}</span>
                  <el-button type="text" size="mini" class="danger_text"
                             @click="removeMember(idx)">移除</el-button>
                </div>
                <div class="member_grid">
                  <el-select v-model="m.agent_id" filterable placeholder="选择成员 Agent"
                             class="member_agent_select">
                    <el-option v-for="a in memberOptions(idx)" :key="a.agent_id"
                               :label="a.display_name || a.name" :value="a.agent_id" />
                  </el-select>
                  <el-input v-model="m.role" maxlength="300"
                            placeholder="写清分工，如：负责招聘渠道拓展、候选人初筛与面试安排" />
                </div>
                <el-input v-if="form.mode === 'distributed'" v-model="m.node_url"
                          class="member_node" placeholder="远端实例地址（可选，如 http://host:port；留空=按节点注册表自动路由）" />
              </div>
              <div v-if="!form.members.length" class="member_empty">尚未添加成员</div>
              <el-button size="mini" icon="el-icon-plus" class="add_member_btn" @click="addMember">
                添加成员
              </el-button>
            </div>
          </el-form-item>
        </el-form>
      </div>
      <div class="drawer_footer">
        <el-button @click="closeEdit">取 消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保 存</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { agentBody, isAdminAcc, isEnterprise } from '../utils'

// 团队管理：队长 + 成员分工（role 注入团队规划提示词）+ 本地/分布式运行模式
export default {
  name: 'AiAgentTeams',
  data() {
    return {
      canWrite: isEnterprise(),
      agentOptions: [],
      queryForm: { name: '', leader_agent_id: '', status: '' },
      tableData: [],
      loading: false,
      currentPage: 1,
      pagesize: 20,
      total: 0,
      editVisible: false,
      isEdit: false,
      saving: false,
      form: this.emptyForm(),
      nodes: [],
      nodeLoading: false,
      rules: {
        name: [{ required: true, message: '请输入团队名称', trigger: 'blur' }],
        leader_agent_id: [{ required: true, message: '请选择队长', trigger: 'change' }]
      }
    }
  },
  computed: {
    onlineNodeCount() {
      return this.nodes.filter(n => n.status === 'active').length
    }
  },
  mounted() {
    if (isAdminAcc()) return
    this.loadAgents()
    this.loadList()
    if (this.canWrite) this.loadNodes()
  },
  methods: {
    // 节点注册表：展示分布式节点在线状态（心跳过期按离线渲染）
    loadNodes() {
      if (!this.canWrite) return
      this.nodeLoading = true
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentTeamsNodesList, agentBody({})).then((res) => {
        if (res.data.code == 0) this.nodes = res.data.data || []
        this.nodeLoading = false
      }).catch(() => { this.nodeLoading = false })
    },
    emptyForm() {
      return {
        team_id: '', name: '', description: '', leader_agent_id: '',
        members: [], mode: 'local', status: 'active'
      }
    },
    onActivate() {
      this.loadAgents()
      this.loadList()
      this.loadNodes()
    },
    loadAgents() {
      return this.$tools.asyncPost(this, this.$baseUrl.aiAgentAgentsList,
        agentBody({ page: 1, limit: 0, status: 'active' })).then((res) => {
        if (res.data.code == 0) {
          this.agentOptions = res.data.data.data || []
          // 同步给父级查询栏的「队长」下拉
          this.$emit('agents-loaded', this.agentOptions)
        }
      }).catch(() => {})
    },
    loadList() {
      this.loading = true
      const data = { page: this.currentPage, limit: this.pagesize }
      if (this.queryForm.name) data.name = this.queryForm.name
      if (this.queryForm.leader_agent_id) data.leader_agent_id = this.queryForm.leader_agent_id
      if (this.queryForm.status) data.status = this.queryForm.status
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentTeamsList, agentBody(data)).then((res) => {
        if (res.data.code == 0) {
          this.tableData = res.data.data.data || []
          this.total = res.data.data.total || 0
        }
        this.loading = false
      }).catch(() => { this.loading = false })
    },
    onSearch() {
      this.currentPage = 1
      this.loadList()
    },
    // 父级查询栏（与子 Tab 同行）下发的查询条件
    applyQuery(q) {
      this.queryForm = {
        name: (q && q.name) || '',
        leader_agent_id: (q && q.leader_agent_id) || '',
        status: (q && q.status) || ''
      }
      this.onSearch()
    },
    resetQuery() {
      this.queryForm = { name: '', leader_agent_id: '', status: '' }
      this.onSearch()
    },
    handleSizeChange(val) { this.pagesize = val; this.loadList() },
    handleCurrentChange(val) { this.currentPage = val; this.loadList() },
    openAdd() {
      this.form = this.emptyForm()
      this.isEdit = false
      this.editVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    openEdit(row) {
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentTeamsGet,
        agentBody({ team_id: row.team_id })).then((res) => {
        if (res.data.code != 0) return
        const d = res.data.data || {}
        this.form = {
          team_id: d.team_id,
          name: d.name || '',
          description: d.description || '',
          leader_agent_id: d.leader_agent_id || '',
          members: (d.members || []).map(m => ({
            agent_id: m.agent_id || '',
            role: m.role || '',
            node_url: m.node_url || ''
          })),
          mode: d.mode || 'local',
          status: d.status || 'active'
        }
        this.isEdit = true
        this.editVisible = true
        this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
      }).catch(() => {})
    },
    closeEdit() {
      this.editVisible = false
    },
    raiseDrawer() {
      this.$nextTick(() => {
        if (this.$el && this.$el.style) this.$el.style.zIndex = 4000
      })
    },
    // 成员可选 Agent：排除队长与已在其他成员行选中的 Agent，保证分工唯一
    memberOptions(idx) {
      const used = this.form.members
        .filter((m, i) => i !== idx)
        .map(m => m.agent_id)
        .filter(Boolean)
      return this.agentOptions.filter(a =>
        a.agent_id !== this.form.leader_agent_id && !used.includes(a.agent_id))
    },
    onLeaderChange() {
      // 队长变更后，已选成员中与队长相同的行清空，避免分工冲突
      this.form.members.forEach((m) => {
        if (m.agent_id === this.form.leader_agent_id) m.agent_id = ''
      })
    },
    addMember() {
      if (this.form.members.length >= 20) {
        this.$message({ type: 'warning', message: '成员数量上限为 20' })
        return
      }
      this.form.members.push({ agent_id: '', role: '', node_url: '' })
    },
    removeMember(idx) {
      this.form.members.splice(idx, 1)
    },
    validateMembers() {
      if (!this.form.members.length) return '请至少添加 1 名成员，并写清各自分工'
      const seen = {}
      for (let i = 0; i < this.form.members.length; i++) {
        const m = this.form.members[i]
        if (!m.agent_id) return `成员 ${i + 1} 未选择 Agent`
        if (seen[m.agent_id]) return `成员 ${i + 1} 与前面的成员重复`
        if (m.agent_id === this.form.leader_agent_id) return `成员 ${i + 1} 不能与队长相同`
        if (!m.role || !m.role.trim()) return `请填写成员 ${i + 1} 的分工说明（会注入规划提示词）`
        if (m.node_url && !/^https?:\/\/[^/\s]+/i.test(m.node_url.trim())) {
          return `成员 ${i + 1} 的远端地址需以 http:// 或 https:// 开头`
        }
        seen[m.agent_id] = true
      }
      return ''
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        const err = this.validateMembers()
        if (err) {
          this.$message({ type: 'warning', message: err })
          return
        }
        this.saving = true
        const payload = {
          name: this.form.name,
          description: this.form.description,
          leader_agent_id: this.form.leader_agent_id,
          mode: this.form.mode,
          status: this.form.status,
          members: this.form.members.map(m => ({
            agent_id: m.agent_id,
            role: m.role.trim(),
            node_url: m.node_url ? m.node_url.trim() : ''
          }))
        }
        if (this.isEdit) payload.team_id = this.form.team_id
        const url = this.isEdit ? this.$baseUrl.aiAgentTeamsEdit : this.$baseUrl.aiAgentTeamsAdd
        this.$tools.asyncPost(this, url, agentBody(payload)).then((res) => {
          if (res.data.code == 0) {
            this.$message({ type: 'success', message: res.data.msg || '操作成功' })
            this.editVisible = false
            if (!this.isEdit) this.currentPage = 1
            this.loadList()
          }
          this.saving = false
        }).catch((err) => {
          this.saving = false
          if (err && err.isAxiosError) this.$message({ type: 'error', message: err.response ? '请求失败，请稍后重试' : '网络异常，请稍后重试' })
        })
      })
    },
    handleDel(row) {
      this.$MessageBox.confirm('删除后该团队不再参与编排，确认删除？', '提示', { type: 'warning' }).then(() => {
        this.$tools.asyncPost(this, this.$baseUrl.aiAgentTeamsDel,
          agentBody({ team_id: row.team_id })).then((res) => {
          if (res.data.code == 0) {
            this.$message({ type: 'success', message: '删除成功' })
            if (this.tableData.length === 1 && this.currentPage > 1) this.currentPage -= 1
            this.loadList()
          }
        }).catch((err) => {
          if (err && err.isAxiosError) this.$message({ type: 'error', message: err.response ? '请求失败，请稍后重试' : '网络异常，请稍后重试' })
        })
      }).catch(() => {})
    },
    // 去对话：切换到对话 Tab 并选中队长员工（团队编排由队长回合自动触发）
    goChat(row) {
      if (!row.leader_agent_id) return
      this.$emit('open-chat', row.leader_agent_id)
    },
    // 团队成员：把队长+成员 id 集合上抛，由父级切到数字员工页并按该团队过滤展示
    goMembers(row) {
      const agentIds = []
      if (row.leader_agent_id) agentIds.push(row.leader_agent_id)
      ;(row.members || []).forEach((m) => {
        if (m.agent_id && !agentIds.includes(m.agent_id)) agentIds.push(m.agent_id)
      })
      if (!agentIds.length) {
        this.$message({ type: 'info', message: '该团队暂无队长或成员' })
        return
      }
      this.$emit('team-members', { teamId: row.team_id, teamName: row.name, agentIds })
    },
    agentName(id) {
      if (!id) return '-'
      const a = this.agentOptions.find(x => x.agent_id === id)
      return a ? (a.display_name || a.name) : id
    },
    fmtTime(s) {
      if (!s) return '-'
      return this.$tools.parserDateTime('datetime', s) || s.substring(0, 19).replace('T', ' ')
    }
  }
}
</script>

<style lang="less" scoped>
.node_panel {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f7f9fc;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.node_head {
  display: flex;
  align-items: center;
  gap: 8px;
  .node_title { font-size: 13px; font-weight: 600; color: #303133; }
}
.node_list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.node_empty { font-size: 12px; color: #909399; }
.teams_wrap {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.node_panel { flex: 0 0 auto; }
.team_cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  padding: 12px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #f9fafd;
  border-top: 1px solid #e9eaec;
  // 滚动条只出现在卡片区内部右侧
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: #ccc;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}
.team_card {
  display: flex;
  flex-direction: column;
  min-height: 230px;
  padding: 12px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 1px 1px 4px #c0bfbf;
}
.card_head {
  display: flex;
  align-items: center;
  gap: 8px;
  .card_icon { color: #8C7EBB; font-size: 16px; }
  .card_name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .card_del {
    color: #c0c4cc;
    cursor: pointer;
    &:hover { color: #f56c6c; }
  }
}
.card_summary {
  height: 36px;
  margin: 8px 0;
  font-size: 12px;
  line-height: 18px;
  color: #909399;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.roster_block {
  flex: 1;
  padding: 8px 10px;
  background: #f7f9fc;
  border-radius: 4px;
  font-size: 12px;
}
.roster_leader {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.roster_members { display: flex; }
.roster_k {
  flex: 0 0 40px;
  color: #909399;
}
.roster_v { color: #303133; }
.leader_name { font-weight: 600; color: #8C7EBB; }
.member_list { flex: 1; min-width: 0; }
.member_item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}
.member_name { flex: 0 0 auto; color: #303133; font-weight: 600; }
.member_role {
  flex: 1;
  min-width: 0;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.roster_empty { color: #c0c4cc; }
.card_meta {
  font-size: 12px;
  color: #606266;
  margin-top: 8px;
  .meta_row {
    display: flex;
    justify-content: space-between;
    padding: 2px 0;
  }
  .meta_k { color: #909399; }
}
.card_actions {
  display: flex;
  align-items: center;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #f2f2f2;
}
// 底部分页：固定在卡片区下方，与订单管理页底部一致
.pager_bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 12px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-sizing: border-box;
}
.cards_empty { grid-column: 1 / -1; }
.team_drawer {
  /deep/ .el-drawer__body {
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
  }
}
.drawer_head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 20px;
  border-bottom: 1px solid #ebeef5;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  .drawer_close {
    font-size: 20px;
    color: #909399;
    cursor: pointer;
    &:hover { color: #f56c6c; }
  }
}
.drawer_body {
  flex: 1;
  padding: 16px 20px 8px;
  overflow: auto;
}
.drawer_footer {
  padding: 12px 20px;
  border-top: 1px solid #ebeef5;
  text-align: right;
}
.drawer_tip { margin-bottom: 14px; }
.field_tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 18px;
}
.member_editor {
  width: 100%;
  padding: 10px;
  background: #f7f9fc;
  border-radius: 4px;
}
.member_row {
  padding: 8px 10px;
  margin-bottom: 8px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.member_row_head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.member_no { font-size: 12px; color: #909399; }
.member_grid {
  display: flex;
  gap: 8px;
}
.member_agent_select { flex: 0 0 180px; }
.member_node { margin-top: 6px; }
.member_empty { padding: 8px 0; font-size: 12px; color: #c0c4cc; }
.add_member_btn { margin-top: 4px; }
.danger_text { color: #f56c6c; }
</style>
